/**
 * Compute a simplified company balance sheet (Bilan comptable simplifié).
 *
 * Returns: { actif, passif, equity, fleet, breakdown } where
 *   - actif  : sum of cash + placements + fleet resale value
 *   - passif : sum of credits remaining + lease remaining commitments + tax debt
 *   - equity = actif - passif (capitaux propres)
 *   - fleet  : the "Patrimoine flotte" subset
 *     {
 *       grossValue:  total resale value of all owned trucks (excl. leased)
 *       mortgageDebt: sum of remainingPrincipal of mortgage-backed loans
 *       netValue:    grossValue - mortgageDebt
 *       count:       vehicles considered
 *       mortgagedCount: vehicles still under mortgage
 *     }
 *
 * Pure function — does NOT touch state. Safe to call from any component.
 */
import { projectAnnualProfit } from './projectAnnualProfit';
import { calculateIS } from '../data/taxes';

// Reproduce the Fleet.jsx sell-price formula so the balance sheet and the
// "Vendre" button stay in sync. Tweaking ONE place updates the other implicitly
// via this util.
export function getVehicleResaleValue(vehicle, totalDays = 0) {
  if (!vehicle) return 0;
  const ageDays = Math.max(0, totalDays - (vehicle.purchaseDay || 0));
  const ageMonths = ageDays / 30;
  const baseSellRate = 0.60;
  const wearPenalty = ((vehicle.wear || 0) / 100) * 0.15;
  const agePenalty = Math.min(ageMonths * 0.01, 0.20);
  const sellRate = Math.max(0.15, baseSellRate - wearPenalty - agePenalty);
  return Math.round((vehicle.price || 0) * sellRate);
}

export function computeBalanceSheet(state) {
  const vehicles = state.vehicles || [];
  const credits = state.credits || [];
  const placements = state.activePlacements || [];
  const totalDays = state.totalDays || 0;

  // --- ACTIF ---
  const cash = Math.round(state.treasury || 0);
  const placementsTotal = placements.reduce((s, p) => s + (p.amount || 0), 0);

  // Owned vehicles only (a leased truck is NOT an asset on the balance sheet —
  // the underlying value belongs to the lessor).
  const ownedVehicles = vehicles.filter(v => !v.isLeased);
  const fleetGross = ownedVehicles.reduce(
    (s, v) => s + getVehicleResaleValue(v, totalDays),
    0
  );
  const mortgagedVehicles = ownedVehicles.filter(v => v.mortgaged);
  const mortgageLoanIds = new Set(
    mortgagedVehicles.map(v => v.mortgageLoanId).filter(Boolean)
  );
  const mortgageDebt = credits
    .filter(c => mortgageLoanIds.has(c.id))
    .reduce((s, c) => s + Math.round(c.remainingPrincipal || 0), 0);
  const fleetNet = fleetGross - mortgageDebt;

  // ── 17/06/2026 — Refonte fiscale (retour user) ──────────────────────
  // Les provisions et réserves fiscales sont des ACTIFS du bilan (de la
  // trésorerie immobilisée temporairement) qui ne sont pas inclus dans
  // `treasury`. Sans elles à l'actif, l'équité est sous-estimée car la
  // dette fiscale (côté passif) est correctement comptabilisée mais sa
  // contrepartie cash provisionnée disparaissait.
  //
  // 3 lignes ajoutées :
  //   • Provision TVA   = `tvaProvisionedBalance` (cash mis de côté chez
  //                       Midal/DAF pour la prochaine échéance TVA)
  //   • Acompte IS      = `isProvisionedBalance`  (cash mis de côté pour
  //                       l'impôt sur les sociétés à venir)
  //   • Réserve fiscale = `fiscalReserveBalance` + intérêts accrus
  //                       (placement rémunéré 3 % servant de tampon)
  // Toutes les valeurs sont des entiers euros, jamais négatives.
  const tvaProvisioned = Math.max(0, Math.round(state.tvaProvisionedBalance || 0));
  const isProvisioned = Math.max(0, Math.round(state.isProvisionedBalance || 0));
  const fiscalReserveTotal = Math.max(0, Math.round(
    (state.fiscalReserveBalance || 0) + (state.fiscalReserveInterestAccrued || 0),
  ));

  const actif = cash + placementsTotal + fleetGross
    + tvaProvisioned + isProvisioned + fiscalReserveTotal;

  // --- PASSIF ---
  const creditsTotal = credits.reduce(
    (s, c) => s + Math.round(c.remainingPrincipal || 0),
    0
  );

  // Leased vehicles: future engagement = remaining monthlies + residual.
  // We do NOT include the 5% penalty here (only payable on early buyout).
  const leasedVehicles = vehicles.filter(v => v.isLeased);
  const leaseEngagement = leasedVehicles.reduce((s, v) => {
    const months = Math.max(0, Math.ceil(((v.leaseEndDay || 0) - totalDays) / 30));
    const monthly = v.leaseMonthlyPayment || 0;
    const residual = v.leaseResidualValue || Math.round((v.price || 0) * 0.25);
    return s + months * monthly + residual;
  }, 0);

  // Tax debt: pending TVA balance + the IS provision implied by annual profit.
  //
  // 11/02/2026 — Retour user prod : « La provision d'IS est souvent bien
  // inférieur à l'IS estimé. La provision de la TVA est plus précise.
  // L'estimation IS est différente entre la vue d'ensemble et la vue
  // actif/passif. »
  //
  // Cause : la provision IS du bilan utilisait `annualProfit * 0.25` (taux
  // standard flat sur le résultat YTD), alors que la vue Fiscalité utilise
  // `calculateIS(annualProfit * 12/month)` (extrapolation à l'année entière
  // + barème 15 % jusqu'à 42 500 € puis 25 % au-delà). Résultat :
  // sous-estimation en début/milieu d'année + écart entre vues.
  //
  // Correction : on aligne le bilan sur la même formule que OverviewTab —
  // extrapolation linéaire du profit annuel + calculateIS pour respecter
  // le barème PME (taux réduit 15 %).
  // 22/07/2026 — Retour user prod : « L'indicateur capitaux propres n'est
  // pas fiable — plombé par l'IS PROJETÉ (j'ai payé 186 708 € d'IS réel,
  // le bilan affichait des montants à plusieurs millions). Distinguer IS
  // réel / IS projeté. »
  // Le passif ne compte plus que l'IS réellement dû sur le résultat YTD
  // (barème PME), net des acomptes déjà versés cette année. L'extrapolation
  // fin d'exercice reste exposée à titre INDICATIF (`isProjected`) mais ne
  // pèse plus sur les capitaux propres.
  const tvaPending = Math.max(0, Math.round(
    (state.tvaCollectedTotal || 0) - (state.tvaDeductibleTotal || 0)
  ));
  const isPaidYtd = (state.isInstallmentsPaid || []).reduce((s, x) => s + (x.amount || 0), 0);
  const isDueYtd = Math.max(0, calculateIS(Math.max(0, state.annualProfit || 0)) - isPaidYtd);
  const { annualIS: isProjected } = projectAnnualProfit(state);
  const taxDebt = tvaPending + isDueYtd;

  const passif = creditsTotal + leaseEngagement + taxDebt;

  // --- CAPITAUX PROPRES ---
  const equity = actif - passif;

  return {
    actif: {
      total: actif,
      cash,
      placements: placementsTotal,
      fleetGross,
      // 17/06/2026 — Nouvelles lignes fiscales (cf. doc utilisateur).
      tvaProvisioned,
      isProvisioned,
      fiscalReserveTotal,
    },
    passif: {
      total: passif,
      credits: creditsTotal,
      leaseEngagement,
      taxDebt,
      tvaPending,
      isProvision: isDueYtd,
      // Indicatif uniquement — non compté dans le passif ni l'équité.
      isProjected,
      isPaidYtd,
    },
    equity,
    fleet: {
      grossValue: fleetGross,
      mortgageDebt,
      netValue: fleetNet,
      count: ownedVehicles.length,
      mortgagedCount: mortgagedVehicles.length,
      leasedCount: leasedVehicles.length,
    },
    // Solvency ratio (capitaux propres / actif × 100).
    // Healthy SMEs typically ≥ 30 %.
    solvencyPct: actif > 0 ? Math.round((equity / actif) * 100) : 0,
    // Debt ratio (passif / actif × 100). Below 60 % is comfortable.
    debtRatioPct: actif > 0 ? Math.round((passif / actif) * 100) : 0,
  };
}
